<template>
  <div class="statistics-view">
    <StatisticsHeader>
      <template #append>
        <div v-if="lastUpdateText" class="header-update">
          {{ lastUpdateText }}
          <v-btn class="ml-1" x-small :loading="statisticRefreshing" icon @click="loadRefreshStatistic">
            <v-icon x-small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </template>
    </StatisticsHeader>
    <div class="d-flex justify-center">
      <!-- Skeleton statt Spinner: Layout bleibt beim Laden stabil -->
      <div v-if="statisticLoading" class="stat-skeleton">
        <div class="stat-skeleton__row">
          <v-skeleton-loader v-for="n in 7" :key="'kpi' + n" type="article" class="stat-skeleton__tile" />
        </div>
        <div class="stat-skeleton__row">
          <v-skeleton-loader v-for="n in 4" :key="'donut' + n" type="card-avatar" class="stat-skeleton__card" />
        </div>
        <v-skeleton-loader type="image" class="stat-skeleton__chart" />
      </div>
      <StateDisplay
        class="mt-12"
        v-else-if="statisticError"
        :loading="false"
        :error="statisticError"
        :no-results="statistic.shops.length === 0"
      />
      <div v-else class="stat-content">
        <StatisticsFilterBar :shops="statistic.shops" v-model="shopFilter" />
        <LiveStatsCard />
        <KpiRow
          :data="filteredStatistic"
          :comparison="filteredComparison"
          :comparison-loading="statisticComparisonLoading"
        />
        <StatisticOverview :data="filteredStatistic" :comparison="filteredComparison" />
        <v-row class="mt-1" :dense="$vuetify.breakpoint.mobile">
          <v-col cols="12">
            <CountryRevenueCard :shops="filteredStatistic.shops" />
          </v-col>
        </v-row>
        <ShopsTable
          :class="$vuetify.breakpoint.mobile ? 'mt-2' : 'mt-3'"
          :data="filteredStatistic.shops"
          :comparison-data="filteredComparison ? filteredComparison.shops : null"
        />
        <ActualCosts :data="filteredStatistic"/>
      </div>
    </div>
  </div>
</template>

<script>
import StatisticOverview from "@/components/Statistics/StatisticOverview/StatisticOverview";
import StateDisplay from "@/components/StateDisplay";
import { mapActions, mapGetters, mapMutations } from "vuex";
import StatisticsHeader from "@/components/Statistics/StatisticsHeader";

import dayjs from "dayjs";
import {getDateFromNow} from "@/utils";
import ActualCosts from "@/components/Settings/Statistics/ActualCosts/ActualCosts.vue";
import KpiRow from "@/components/Statistics/KpiRow.vue";
import StatisticsFilterBar from "@/components/Statistics/StatisticsFilterBar.vue";
import LiveStatsCard from "@/components/Statistics/LiveStatsCard.vue";
import ShopsTable from "@/components/Statistics/ShopsTable.vue";
import CountryRevenueCard from "@/components/Statistics/CountryRevenueCard.vue";
const timezone = require("dayjs/plugin/timezone"); // Import timezone plugin
const utc = require('dayjs/plugin/utc'); // Import UTC plugin

dayjs.extend(utc); // Enable UTC plugin
dayjs.extend(timezone); // Enable timezone plugin

export default {
  name: "StatisticsView",
  components: {
    ActualCosts, StatisticsHeader, StateDisplay, StatisticOverview,
    KpiRow, StatisticsFilterBar, ShopsTable, CountryRevenueCard, LiveStatsCard,
  },
  data() {
    return {
      shopFilter: [],
    };
  },
  computed: {
    ...mapGetters([
      "statisticLoading",
      "statisticError",
      "statisticErrorMessage",
      "statisticRange",
      "statistic",
      "statisticComparison",
      "statisticComparisonLoading",
      "statisticRefreshing"
    ]),
    /** Shop-Filter anwenden. KI-Kosten sind nicht Shop-gebunden — bei aktivem
     *  Filter werden sie ausgeblendet statt falsch zugeordnet. */
    filteredStatistic() {
      return this.applyShopFilter(this.statistic);
    },
    filteredComparison() {
      return this.statisticComparison
        ? this.applyShopFilter(this.statisticComparison)
        : null;
    },
    lastUpdateText() {
      return this.statistic?.information?.lastUpdate ? `Last Update: ${getDateFromNow(this.statistic.information.lastUpdate)}` : null
    },
  },
  mounted() {
    this.setCurrentDate();
    this.loadStatistic();
  },
  methods: {
    ...mapActions(["loadStatistic", "refreshStatistic"]),
    applyShopFilter(statistic) {
      if (!statistic || !this.shopFilter.length) return statistic;
      return {
        ...statistic,
        shops: (statistic.shops || []).filter((shop) =>
          this.shopFilter.includes(shop.shopCode)
        ),
        aiCosts: { total: 0, byKey: {} },
      };
    },
    ...mapMutations(["setStatisticRange"]),
    loadRefreshStatistic() {
      this.refreshStatistic(this.statisticRange.min)
    },
    setCurrentDate() {
      const currentLocalizedDate = dayjs().tz("Europe/Berlin", true);

      this.setStatisticRange({
        min: currentLocalizedDate.startOf("day").format(),
        max: currentLocalizedDate.endOf("day").format(),
      });
      // this.setStatisticRange({
      //   min: "2024-06-05T00:00:00+02:00",
      //   max: "2024-06-05T00:00:02+02:00",
      // });
    },
  },
};
</script>

<style lang="scss" scoped>
.statistics-view {
  width: 100%;
  padding-bottom: 48px;

  @include md() {
    padding: 0 10px;
  }

  /* Einheitlicher Karten-Look (wie "Umsatz je Land"): weicher Schatten,
     gleicher Radius und Titel-Typo für ALLE Karten der Seite */
  ::v-deep .v-card {
    border-radius: 14px !important;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07) !important;
  }

  ::v-deep .v-card__title {
    font-size: 16px;
    font-weight: 700;
  }
}

/* Inhalt darf nie breiter als der Viewport werden — breite Tabellen
   scrollen innerhalb ihrer Karte statt die Seite zu sprengen */
.stat-content {
  width: 100%;
  max-width: 1560px;
  min-width: 0;

  ::v-deep .v-data-table__wrapper,
  ::v-deep .v-data-table {
    overflow-x: auto;
  }
}

.stat-skeleton {
  width: 100%;
  max-width: 1400px;

  &__row {
    display: flex;
    gap: 10px;
    margin-bottom: 14px;
  }

  &__tile {
    flex: 1;
    height: 96px;
  }

  &__card {
    flex: 1;
    height: 260px;
  }

  &__chart {
    height: 320px;
  }
}

.header-update {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}
</style>
